// Flights scheduler — view registry, date maths and colour resolution.
//
// Mirrored from src/bookings/calendar.ts, which is the v6 port of the legacy
// dhtmlxScheduler in mod_flight_booking. This file is the same GEOMETRY over a
// different row: `flights_info` instead of `flight_booking`.
//
// G2-845: scheduler creation and schedule-only move/resize now use the existing
// flight CRUD contract. calendarGestures.ts rejects intervals that the single
// date column cannot represent and never derives recorded hours from geometry.
//
// ── WHAT MEASURED, AND WHERE ───────────────────────────────────────────────
// module_g2_demo, 2026-08-31 (COUNT(*), filtered `active = 1`):
//   38 active TOP-LEVEL flights (41 active rows minus 3 legs), all 38 with a
//   real `date` (zero-dates: 0) and an `aircraft_id`, 34 with `dep_time` and
//   `arr_time`, 31 with `hours`, 37 with `pic`. 30 distinct aircraft. Span
//   2026-07-16 .. 2026-08-30.
// That is what makes the day-column views (which need a time) and the section
// views (which need an aircraft) both real here rather than decorative.

import type { BoardToolbarViewGroup } from "../components/BoardToolbar";
import { NO_TIMES_TEXT, allDayDates, toneForIndex } from "../components/schedulerBoard";
import type { EventTone } from "../components/schedulerBoard";
import type { Flight } from "./types";

// ---------------------------------------------------------------- views

export interface CalendarView {
  /** Toolbar label. Mirrors the bookings labels so the two modules read alike. */
  label: string;
  /** What the VIEW button caps to when this view is selected. */
  short: string;
  /** Which of the three dropdown FAMILIES this view belongs to. */
  family: "calendar" | "timeline" | "other";
  /** Days shown per page. */
  days: number;
  /**
   * Grid orientation.
   *  "day-columns"         — day columns x hour rows
   *  "section-rows"        — aircraft rows x hour columns
   *  "section-columns"     — aircraft columns x hour rows
   *  "section-day-columns" — aircraft rows x DAY columns (the Gantt geometry)
   */
  orientation:
    | "day-columns"
    | "section-rows"
    | "section-columns"
    | "section-day-columns";
}

/**
 * The view set. Keys are the persisted values (see modes.ts
 * readRememberedCalendarView) so they must stay stable.
 *
 * Mirrors bookings' set MINUS the two whose keys exist there only to preserve
 * an older persisted meaning (`workweek`/`week` carry a legacyMode that no
 * longer matches their orientation). Flights has no persisted history to
 * preserve, so its keys say what they render.
 */
export const CALENDAR_VIEWS: { [key: string]: CalendarView } = {
  day: { label: "Day", short: "Day", family: "calendar", days: 1, orientation: "day-columns" },
  workweek: { label: "4 Day", short: "4 Days", family: "calendar", days: 4, orientation: "day-columns" },
  week: { label: "Week", short: "Week", family: "calendar", days: 7, orientation: "day-columns" },
  timeline: { label: "Aircraft Day", short: "Aircraft Day", family: "timeline", days: 1, orientation: "section-rows" },
  // ─── The Gantt family. Aircraft ROWS x DAY columns ────────────────────
  //
  // A FLIGHT BAR IS ALWAYS ONE COLUMN WIDE, and that is not a defect. A
  // booking carries `date`..`date_to` and can span days; `flights_info` has
  // no `date_to` column at all, so a flight occupies exactly its own date.
  // dayIndexSpan below therefore always returns from === to here. These views
  // still earn their place — they answer "which aircraft flew on which days
  // over this span", which the 1-day views cannot — but do not read the
  // single-column bars as a rendering bug.
  timeline_workweek: { label: "4 Day", short: "Timeline 4 Days", family: "timeline", days: 4, orientation: "section-day-columns" },
  timeline_week: { label: "Week", short: "Timeline Week", family: "timeline", days: 7, orientation: "section-day-columns" },
  timeline_4week: { label: "4 Weeks", short: "Timeline 4 Weeks", family: "timeline", days: 28, orientation: "section-day-columns" },
  unit: { label: "Vertical", short: "Vertical", family: "other", days: 1, orientation: "section-columns" },
};

export const isCalendarView = (key: string): boolean =>
  Object.prototype.hasOwnProperty.call(CALENDAR_VIEWS, key);

/** Grouped for the toolbar's view select, matching bookings' grouping. */
export const VIEW_FAMILIES: { key: CalendarView["family"]; label: string }[] = [
  { key: "calendar", label: "Calendar" },
  { key: "timeline", label: "Timeline" },
  { key: "other", label: "Other" },
];

/**
 * The VIEW menu as Pattern 441 sections: one group per family, in family
 * order, options in `CALENDAR_VIEWS` key order. Empty families are dropped.
 */
export function viewGroups(): BoardToolbarViewGroup[] {
  return VIEW_FAMILIES.map((fam) => ({
    label: fam.label,
    options: Object.entries(CALENDAR_VIEWS)
      .filter(([, v]) => v.family === fam.key)
      .map(([key, v]) => ({ key, label: v.label, short: v.short })),
  })).filter((g) => g.options.length > 0);
}

/** Listing routes share this scheduler, with the same view split as Bookings. */
export const MODE_VIEW_FAMILIES: Record<string, CalendarView["family"][]> = {
  calendar: ["calendar"],
  timeline: ["timeline", "other"],
};

export function isViewInMode(key: string, mode: string): boolean {
  if (!isCalendarView(key)) return false;
  const families = Object.prototype.hasOwnProperty.call(MODE_VIEW_FAMILIES, mode)
    ? MODE_VIEW_FAMILIES[mode] : MODE_VIEW_FAMILIES.calendar;
  return families.includes(CALENDAR_VIEWS[key].family);
}

export function viewGroupsForMode(mode: string): BoardToolbarViewGroup[] {
  return viewGroups().map((group) => ({
    ...group, options: group.options.filter((option) => isViewInMode(option.key, mode)),
  })).filter((group) => group.options.length > 0);
}

// ---------------------------------------------------------------- config

/**
 * Visible hour window. HARDCODED, and unlike bookings that is not a fallback.
 *
 * Bookings reads `flight_booking_first_hour` / `_last_hour` through useConfig.
 * Flights has NO equivalent: of the 502 `config_function = 'flights'` rows on
 * module_g2_demo (2026-08-31), none is a calendar window — every variable
 * matching "hour" is about FLIGHT hours (`flights_engine_hour_field_name`,
 * `flights_hours_decimal`, `flights_mr_hours_*`) and every one matching
 * "calendar" is a Google Calendar integration flag. Verified against a
 * POSITIVE CONTROL in the same query so the empty result cannot be a wrong
 * column name or a wrong prefix: `flight_booking_first_hour` = 8,
 * `flight_booking_last_hour` = 21, `flight_booking_default_view` = workweek
 * all came back from that read. So there is nothing live to prefer and these
 * constants are the only source.
 *
 * 07..23, NOT bookings' 08..21. Sized to the DATA, because a window narrower
 * than the flights silently drops blocks off the grid — which reads as "no
 * flight that day", the worst failure this surface can have. Measured over
 * active flights on module_g2_demo 2026-08-31: earliest `dep_time` 08:00,
 * latest `arr_time` 22:58. A 21:00 ceiling would have clipped real arrivals.
 * One hour of headroom each side; re-measure before narrowing it.
 */
export const DEFAULT_FIRST_HOUR = 7;
export const DEFAULT_LAST_HOUR = 23;

/** Opening view. The 4-day calendar, matching bookings' measured default. */
export const DEFAULT_VIEW = "workweek";

export const MODE_DEFAULT_VIEW: Record<string, string> = {
  calendar: DEFAULT_VIEW,
  timeline: "timeline",
};

// ------------------------------------------------------- section axis

/**
 * The catch-all section, for flights with no aircraft assigned.
 *
 * Bookings labels this row "Notes / No Aircraft" because legacy's timeline
 * axis has an unconditional notes row (new_display.inc:401-406) that also
 * collects aircraft-less bookings. `flights_info` has no note rows — there is
 * no `source_type` on it — so this row carries ONLY the second half of that
 * job, and is labelled for what it actually holds.
 *
 * On g2 today it is empty (aircraft_id is set on all 38 active top-level
 * flights). It renders anyway: a flight whose aircraft is unset would
 * otherwise be fetched, counted and then drawn nowhere, which reads as data
 * loss. Same reasoning as bookings' `inSection`.
 */
export const NO_AIRCRAFT_SECTION_ID = 0;
export const NO_AIRCRAFT_SECTION_LABEL = "No Aircraft";

export interface TimelineSection {
  /** `NO_AIRCRAFT_SECTION_ID` for the catch-all row, else the aircraft id. */
  id: number;
  label: string;
  isCatchAll: boolean;
}

/**
 * Section axis for the aircraft-row views: the catch-all row first, then the
 * fleet in the order the caller supplies (which must be the SAME order
 * `blockTone` is given, or the colours stop following the aircraft).
 */
export function buildSections(
  fleet: { id: number; registration?: string | null }[]
): TimelineSection[] {
  return [
    { id: NO_AIRCRAFT_SECTION_ID, label: NO_AIRCRAFT_SECTION_LABEL, isCatchAll: true },
    ...fleet.map((a) => ({
      id: a.id,
      label: String(a.registration ?? `#${a.id}`),
      isCatchAll: false,
    })),
  ];
}

/** The flight's aircraft id, from the resolved join or the raw column. */
export function flightAircraftId(f: Flight): number | null {
  const joined = f.aircraft_obj?.id;
  if (joined != null && joined > 0) return joined;
  return f.aircraft_id != null && f.aircraft_id > 0 ? f.aircraft_id : null;
}

/** Does this flight belong in this section? */
export function inSection(f: Flight, section: TimelineSection): boolean {
  const acId = flightAircraftId(f);
  return section.isCatchAll ? acId == null : acId === section.id;
}

// ---------------------------------------------------------------- colour

/**
 * Tone for one block. `sectionIds` must be the SAME ordered list the grid
 * renders its sections from, so the tone follows the aircraft rather than the
 * row position — a plain `aircraft_id % tones.length` would recolour every
 * block whenever the fleet changes. A flight with no aircraft falls to tone 0
 * (`toneForIndex` clamps a negative index), which is what the catch-all row
 * shows.
 */
export function blockTone(
  aircraftId: number | null,
  sectionIds: number[]
): EventTone {
  return toneForIndex(aircraftId == null ? -1 : sectionIds.indexOf(aircraftId));
}

// ---------------------------------------------------------------- dates

/** "YYYY-MM-DD" for a Date, in LOCAL time (never toISOString — that is UTC). */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseIsoDate(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export function addDays(iso: string, n: number): string {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + n);
  return toIsoDate(d);
}

/**
 * The visible date range for a view anchored on `anchor`.
 *
 * `>= 7` week-aligns to MONDAY — DHTMLX's `start_on_monday` default paints the
 * week Mon–Sun and duty weeks are ISO — so the fetched range, the toolbar
 * label and the painted columns are the same seven days. It used to align to
 * Sunday (`-d.getDay()`), which left the displayed Sunday outside the fetch.
 * The 1-day and 4-day views start ON the anchor.
 */
export function viewRange(view: CalendarView, anchor: string): string[] {
  let start = anchor;
  if (view.days >= 7) {
    const d = parseIsoDate(anchor);
    start = addDays(anchor, -((d.getDay() + 6) % 7));
  }
  return Array.from({ length: view.days }, (_, i) => addDays(start, i));
}

/** First visible day of the view — what the scheduler must be anchored on. */
export function viewStart(view: CalendarView, anchor: string): string {
  return viewRange(view, anchor)[0];
}

/** First and last visible day, for the toolbar period label and the fetch. */
export function viewSpan(
  view: CalendarView,
  anchor: string
): { start: string; end: string } {
  const days = viewRange(view, anchor);
  return { start: days[0], end: days[days.length - 1] };
}

/** Column header for a day in the Gantt — "Sun 30". */
export function dayColumnLabel(iso: string): { weekday: string; day: string } {
  const d = parseIsoDate(iso);
  return {
    weekday: d.toLocaleDateString("en-NZ", { weekday: "short" }),
    day: String(d.getDate()),
  };
}

/** Saturday or Sunday — banded in the Gantt so week boundaries are readable. */
export function isWeekend(iso: string): boolean {
  const wd = parseIsoDate(iso).getDay();
  return wd === 0 || wd === 6;
}

/** Which column index holds today, or -1 when today is outside the span. */
export function todayIndex(days: string[]): number {
  return days.indexOf(toIsoDate(new Date()));
}

/**
 * Does this flight occupy `isoDate`?
 *
 * A flight has ONE date — there is no `date_to` on `flights_info` — so this
 * is an equality, not the range test bookings needs. The MySQL zero-date is
 * excluded explicitly rather than compared: a row carrying "0000-00-00"
 * would otherwise match a caller that happened to ask for that string, and a
 * zero-date is never a real day (memory feedback_never_insert_mysql_zero_date).
 * g2 has none today; that is a fact about the data, not a guarantee about it.
 */
export function occupiesDate(f: Flight, isoDate: string): boolean {
  const d = String(f.date ?? "").slice(0, 10);
  if (!d || d === "0000-00-00") return false;
  return d === isoDate;
}

/**
 * The [firstIndex, lastIndex] columns a flight occupies inside `days`, or
 * null when it occupies none of them. Always a single column — see the note
 * on the Gantt family above — but derived by scanning `occupiesDate` rather
 * than asserted, so the zero-date handling lives in exactly one place.
 */
export function dayIndexSpan(
  f: Flight,
  days: string[]
): { from: number; to: number } | null {
  let from = -1;
  let to = -1;
  for (let i = 0; i < days.length; i++) {
    if (!occupiesDate(f, days[i])) continue;
    if (from < 0) from = i;
    to = i;
  }
  return from < 0 ? null : { from, to };
}

// ------------------------------------------------------- event geometry

/** "HH:MM[:SS]" -> minutes since midnight. Null for an unparseable value. */
export function toMinutes(hhmmish: string | null | undefined): number | null {
  if (!hhmmish) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmmish));
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return h * 60 + min;
}

export function hhmm(hhmmish: string | null | undefined): string {
  const mins = toMinutes(hhmmish);
  if (mins == null) return "";
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

/**
 * Fractional position of a flight inside the visible hour window, as
 * { top, height } in 0..1. Returns null when the flight has no usable
 * `dep_time` — a timeless flight is NOT clamped to 00:00, which would stack
 * every one of them in the top-left cell and read as a 06:00 departure that
 * was never recorded. The caller lists them separately instead.
 *
 * 4 of g2's 38 active top-level flights have no dep_time, so this branch is
 * live on the current data, not defensive.
 */
export function timeSpan(
  f: Flight,
  firstHour: number,
  lastHour: number
): { top: number; height: number } | null {
  const start = toMinutes(f.dep_time);
  const stop = toMinutes(f.arr_time);
  if (start == null) return null;
  const windowStart = firstHour * 60;
  const windowEnd = lastHour * 60;
  const span = windowEnd - windowStart;
  if (span <= 0) return null;
  // An arrival at or before the departure (a blank arr_time, or a flight that
  // crossed midnight and wrapped) still gets a readable block rather than a
  // zero-height sliver.
  const end = stop != null && stop > start ? stop : start + 30;
  const top = (Math.max(start, windowStart) - windowStart) / span;
  const height = (Math.min(end, windowEnd) - Math.max(start, windowStart)) / span;
  if (height <= 0) return null;
  return { top, height: Math.max(height, 0.012) };
}

/**
 * Block label. Every part is omitted when absent rather than rendered as a
 * placeholder — a flight with no PIC recorded shows no PIC, not "—".
 *
 * `type_obj`/`status_obj` are the backend's resolved joins and are preferred
 * over the raw columns. ⚠️ `flights_info.status` is HALF-MIGRATED on g2: 34
 * of 41 active rows hold the literal string 'Completed' and 7 hold a types
 * id-as-string (7442-7449). FlightInfo.status_obj joins by
 * `cast(foreign(Type.id), String)`, so it resolves for those 7 and is NULL
 * for the other 34. `statusText` below therefore falls back to the raw column
 * when it is not numeric — which renders 'Completed' correctly — and drops it
 * when it IS numeric and unresolved, because a bare "7443" on a block is a
 * raw id shown to an operator and a wrong label is worse than a missing one.
 */
export function statusText(f: Flight): string {
  const resolved = f.status_obj?.name;
  if (resolved) return String(resolved);
  const raw = String(f.status ?? "").trim();
  if (!raw) return "";
  return /^\d+$/.test(raw) ? "" : raw;
}

export function blockLabel(f: Flight): string {
  const times = [hhmm(f.dep_time), hhmm(f.arr_time)].filter(Boolean).join(" - ");
  const head = [times, f.flight_code || `#${f.id}`].filter(Boolean).join(" / ");
  const tail = [
    f.aircraft_obj?.registration,
    f.pic_user?.gecos ?? f.pic,
    statusText(f),
    f.client_company?.name,
  ]
    .filter(Boolean)
    .join(" ");
  return [head, tail].filter(Boolean).join(" ");
}

/**
 * The time line on a block, bar or tooltip — the RECORDED times, never the
 * block's geometry: a missing half is an em dash, both missing says so. (H252:
 * #3454 and #3455 read "07:00-23:00", the window a timeless flight used to be
 * stretched across, as if it had been booked for it.)
 */
export function blockTimeText(f: Flight): string {
  const dep = hhmm(f.dep_time);
  const arr = hhmm(f.arr_time);
  if (!dep && !arr) return NO_TIMES_TEXT;
  return `${dep || "—"} – ${arr || "—"}`;
}

/**
 * How long a block with ONE recorded time is drawn — the same half hour
 * `timeSpan` above gives a flight whose arrival is missing.
 */
export const ONE_TIME_BLOCK_MINUTES = 30;

/**
 * Where a flight sits on the scheduler.
 *
 *  - Both times: dep..arr, exactly as before; an arrival at or before the
 *    departure (a wrap past midnight, or a bad row) gets an hour.
 *  - NEITHER time: ALL-DAY (Aaron, 2026-09-30: "should be all day events") —
 *    the all-day row / a full-day band, never 07:00-23:00.
 *  - ONE time: a short timed block ANCHORED ON THE TIME THAT WAS RECORDED —
 *    departure only: dep..dep+30min; arrival only: arr-30min..arr. The one real
 *    time places the flight in its day, which an all-day event would throw
 *    away; stretching to the window's edge (the old 23:00 / 07:00) invents the
 *    other end. The label (`blockTimeText`) prints the missing half as "—", so
 *    the half hour is never read as a recorded time.
 *
 * Null when the flight has no usable date.
 */
export function flightEventSpan(
  f: Flight
): { start: Date; end: Date; allDay: boolean } | null {
  const date = String(f.date ?? "").slice(0, 10);
  const dep = hhmm(f.dep_time);
  const arr = hhmm(f.arr_time);
  if (!dep && !arr) {
    const whole = allDayDates(date);
    return whole ? { ...whole, allDay: true } : null;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date === "0000-00-00") return null;
  const oneBlock = ONE_TIME_BLOCK_MINUTES * 60000;
  if (dep && !arr) {
    const start = new Date(`${date}T${dep}:00`);
    return { start, end: new Date(start.getTime() + oneBlock), allDay: false };
  }
  if (!dep && arr) {
    const end = new Date(`${date}T${arr}:00`);
    return { start: new Date(end.getTime() - oneBlock), end, allDay: false };
  }
  const start = new Date(`${date}T${dep}:00`);
  let end = new Date(`${date}T${arr}:00`);
  if (Number.isNaN(start.getTime())) return null;
  if (Number.isNaN(end.getTime()) || end.getTime() <= start.getTime()) {
    end = new Date(start.getTime() + 3600000);
  }
  return { start, end, allDay: false };
}

/** Block title — the registration, else the flight code, else "#id". */
export function eventTitle(f: Flight): string {
  return f.aircraft_obj?.registration ?? f.flight_code ?? `#${f.id}`;
}
